import type { CsiSystem } from "design-scope";

/**
 * UniFormat codes are stored without spaces (`B3010`). MasterFormat codes are
 * stored in pairs (`07 31 13`). Search and import accept either spacing.
 */

function compact(value: string): string {
  return value.replace(/\s+/g, "");
}

/** Letter-optional UniFormat number, including a `.NN` sub-level. */
function isUniFormatCode(value: string): boolean {
  return /^[A-Za-z]?\d*(?:\.\d+)?$/.test(value) && /[A-Za-z0-9]/.test(value);
}

/** Numeric MasterFormat number, including a `.NN` sub-level. */
function isMasterFormatCode(value: string): boolean {
  return /^\d+(?:\.\d+)?$/.test(value);
}

export function normalizeUniFormatToken(token: string): string | null {
  const stripped = compact(token.trim());
  if (!isUniFormatCode(stripped)) return null;
  return stripped;
}

export function normalizeMasterFormatToken(token: string): string | null {
  const stripped = compact(token.trim());
  const match = stripped.match(/^(\d+)(\.\d+)?$/);
  if (!match || !isMasterFormatCode(stripped)) return null;
  const groups = match[1].match(/.{1,2}/g) ?? [match[1]];
  return `${groups.join(" ")}${match[2] ?? ""}`;
}

function mapCodeList(
  value: string,
  mapToken: (token: string) => string | null
): string {
  if (!value.trim()) return value;
  return value
    .split(",")
    .map((part) => {
      const token = part.trim();
      if (!token) return "";
      return mapToken(token) ?? token;
    })
    .join(", ");
}

export function normalizeUniFormatCodeCell(value: string): string {
  return mapCodeList(value, normalizeUniFormatToken);
}

export function normalizeMasterFormatCodeCell(value: string): string {
  return mapCodeList(value, normalizeMasterFormatToken);
}

/** Number strings to match for a typed classification query. */
export function classificationNumberSearchValues(
  query: string,
  system: CsiSystem
): string[] {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const values = [trimmed];
  const add = (next: string | null) => {
    if (next && !values.includes(next)) values.push(next);
  };
  if (system === "UF") {
    add(normalizeUniFormatToken(trimmed));
  } else {
    const stripped = compact(trimmed);
    add(normalizeMasterFormatToken(stripped));
    if (stripped !== trimmed) add(stripped);
  }
  return values;
}
